import { useState, ReactNode } from 'react';
import { Award, CheckCircle2, Loader2, X, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import moment from 'moment';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useBadgeVerificationQuery,
} from '@/client/api/badges/badges.query';
import {
  useApproveBadgeApplicationMutation,
  useRejectBadgeApplicationMutation,
} from '@/client/api/badges/badges.mutation';
import {
  APPLICATION_STATUS_BADGE,
  BadgeApplication,
  parseAsidJson,
} from '@/client/types/badges';
import EvidencePreview from './EvidencePreview';

interface ApplicationReviewDrawerProps {
  application: BadgeApplication | null;
  isOpen: boolean;
  onClose: () => void;
}

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="break-all text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const ApplicationReviewDrawer = ({
  application,
  isOpen,
  onClose,
}: ApplicationReviewDrawerProps) => {
  const [notes, setNotes] = useState('');
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);

  const asids = parseAsidJson(application?.asid_json);
  const vid = application?.vid ? Number(application.vid) : null;
  const { data: verification, isLoading: verificationLoading } = useBadgeVerificationQuery(
    isOpen ? vid : null,
  );

  const approveMutation = useApproveBadgeApplicationMutation();
  const rejectMutation = useRejectBadgeApplicationMutation();
  const isPending = approveMutation.isPending || rejectMutation.isPending;

  if (!isOpen || !application) return null;

  const statusClass =
    APPLICATION_STATUS_BADGE[application.status] ?? 'bg-gray-100 text-gray-700';
  const canDecide = application.status === 'under_review' || application.status === 'pending';
  const checklist = verification?.checklist ?? [];

  const handleClose = () => {
    if (isPending) return;
    setNotes('');
    setDecision(null);
    onClose();
  };

  const submitDecision = async () => {
    if (!decision) return;
    if (decision === 'reject' && !notes.trim()) return;

    const toastId = toast.loading(
      decision === 'approve' ? 'Approving application…' : 'Rejecting application…',
    );
    try {
      if (decision === 'approve') {
        await approveMutation.mutateAsync({
          baid: application.baid,
          reviewer_notes: notes.trim() || undefined,
        });
        toast.success('Badge awarded', {
          id: toastId,
          description: `${application.badge_name ?? 'Badge'} approved and awarded.`,
          richColors: true,
        });
      } else {
        await rejectMutation.mutateAsync({
          baid: application.baid,
          reviewer_notes: notes.trim(),
        });
        toast.success('Application rejected', {
          id: toastId,
          description: 'The applicant has been notified via the verification trail.',
          richColors: true,
        });
      }
      setDecision(null);
      setNotes('');
      onClose();
    } catch (error: any) {
      toast.error(decision === 'approve' ? 'Approval failed' : 'Rejection failed', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
      });
    }
  };

  return (
    <>
      <Drawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <DrawerContent className="mx-auto flex max-h-[92vh] max-w-2xl flex-col">
          <DrawerHeader className="border-b border-gray-100 text-left">
            <div className="flex items-start justify-between gap-3">
              <div>
                <DrawerTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-amber-600" />
                  {application.badge_name || `Badge #${application.btid}`}
                </DrawerTitle>
                <DrawerDescription>
                  Application baid {application.baid}
                </DrawerDescription>
              </div>
              <DrawerClose asChild>
                <Button variant="ghost" size="icon" disabled={isPending}>
                  <X className="h-4 w-4" />
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>

          <div className="flex-1 space-y-6 overflow-y-auto p-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoRow
                label="Status"
                value={
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass}`}
                  >
                    {application.status.replace(/_/g, ' ')}
                  </span>
                }
              />
              <InfoRow
                label="Submitted"
                value={
                  application.date_submitted
                    ? moment(application.date_submitted).format('YYYY-MM-DD HH:mm')
                    : '—'
                }
              />
              <InfoRow
                label="Applicant"
                value={
                  application.uid
                    ? `User uid ${application.uid}`
                    : application.oid
                      ? `Org oid ${application.oid}`
                      : '—'
                }
              />
              <InfoRow label="Badge type" value={`btid ${application.btid}`} />
              {vid ? <InfoRow label="Verification" value={`vid ${vid}`} /> : null}
            </div>

            {application.notes ? (
              <div>
                <p className="mb-1 text-xs font-medium text-gray-500">Applicant notes</p>
                <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-800">
                  {application.notes}
                </p>
              </div>
            ) : null}

            {application.reviewer_notes ? (
              <div>
                <p className="mb-1 text-xs font-medium text-gray-500">Reviewer notes</p>
                <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-800">
                  {application.reviewer_notes}
                </p>
              </div>
            ) : null}

            <div>
              <p className="mb-2 text-sm font-semibold text-gray-900">
                Evidence ({asids.length})
              </p>
              {asids.length === 0 ? (
                <p className="text-sm text-gray-500">No attachments on this application.</p>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {asids.map((asid) => (
                    <EvidencePreview key={asid} asid={asid} />
                  ))}
                </div>
              )}
            </div>

            {vid ? (
              <div>
                <p className="mb-2 text-sm font-semibold text-gray-900">Verification checklist</p>
                {verificationLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : checklist.length === 0 ? (
                  <p className="text-sm text-gray-500">No checklist items.</p>
                ) : (
                  <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                    {checklist.map((item, idx) => (
                      <li
                        key={item.vcid ?? item.item_key ?? idx}
                        className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                      >
                        <span className="font-medium text-gray-800">
                          {String(item.item_key ?? `Item ${idx + 1}`)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {String(item.status ?? '—')}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}

            {canDecide ? (
              <div className="space-y-1">
                <Label htmlFor="reviewer-notes">Reviewer notes</Label>
                <textarea
                  id="reviewer-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional for approve; required for reject"
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
          </div>

          {canDecide ? (
            <DrawerFooter className="border-t border-gray-100">
              <div className="flex gap-2">
                <Button
                  className="flex-1 gap-1.5"
                  disabled={isPending}
                  onClick={() => setDecision('approve')}
                >
                  {isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  Approve
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 gap-1.5 text-red-600"
                  disabled={isPending}
                  onClick={() => setDecision('reject')}
                >
                  <XCircle className="h-4 w-4" />
                  Reject
                </Button>
              </div>
            </DrawerFooter>
          ) : null}
        </DrawerContent>
      </Drawer>

      <AlertDialog
        open={Boolean(decision)}
        onOpenChange={(next) => {
          if (!next) setDecision(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {decision === 'approve' ? 'Approve and award badge?' : 'Reject this application?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {decision === 'approve'
                ? 'This passes the linked verification (if any) and awards the badge. The award is idempotent.'
                : 'This fails the linked verification and marks the application rejected. Reviewer notes are required.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {decision === 'reject' && !notes.trim() ? (
            <p className="text-sm text-red-600">Add reviewer notes before rejecting.</p>
          ) : null}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={decision === 'reject' ? 'bg-red-600 hover:bg-red-700' : undefined}
              disabled={
                isPending || (decision === 'reject' && !notes.trim())
              }
              onClick={(e) => {
                e.preventDefault();
                void submitDecision();
              }}
            >
              {isPending ? 'Working…' : decision === 'approve' ? 'Approve' : 'Reject'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ApplicationReviewDrawer;
